<template>
  <div class="search_overlay" :class="{ active: isOpen }" @click.self="close">
    <div class="search_overlay_content">
      <div class="search_overlay_header">
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          class="search_overlay_input"
          placeholder="Search..."
          @input="onSearchInput"
          @keydown.esc="close"
        >
        <button class="search_overlay_close" @click="close" aria-label="Close search">
        </button>
      </div>

      <!-- Loading indicator -->
      <div v-if="loading" class="search_overlay_loading">
        Searching...
      </div>

      <!-- Error message -->
      <div v-if="error" class="search_overlay_error">
        {{ error }}
      </div>

      <!-- Results -->
      <div v-if="hasResults" class="search_overlay_results">
        <!-- Cameras section -->
        <div v-if="results.cameras && results.cameras.length" class="search_overlay_section">
          <h3 class="search_overlay_section_title">Cameras ({{ results.cameras.length }})</h3>
          <ul class="search_overlay_list">
            <li
              v-for="camera in results.cameras"
              :key="camera.document.id"
              class="search_overlay_item"
            >
              <a :href="getRelativeLink(camera.document.Link)" class="search_overlay_link" @click="close">
                <div class="search_overlay_item_title" v-html="getHighlight(camera, 'Title')"></div>
                <div v-if="camera.document.ProductType" class="search_overlay_item_meta">
                  {{ camera.document.ProductType }}
                </div>
              </a>
            </li>
          </ul>
        </div>

        <!-- Pages section -->
        <div v-if="results.pages && results.pages.length" class="search_overlay_section">
          <h3 class="search_overlay_section_title">Pages ({{ results.pages.length }})</h3>
          <ul class="search_overlay_list">
            <li
              v-for="page in results.pages"
              :key="page.document.id"
              class="search_overlay_item"
            >
              <a :href="getRelativeLink(page.document.Link)" class="search_overlay_link" @click="close">
                <div class="search_overlay_item_title" v-html="getHighlight(page, 'Title')"></div>
                <div v-if="getHighlight(page, 'Content')" class="search_overlay_item_highlight" v-html="getHighlight(page, 'Content')"></div>
                <div v-else-if="page.document.Summary" class="search_overlay_item_summary">{{ page.document.Summary }}</div>
              </a>
            </li>
          </ul>
        </div>

        <!-- No results message -->
        <div v-if="searchQuery && !loading && !hasAnyResults" class="search_overlay_no_results">
          No results found for "{{ searchQuery }}"
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';

const props = defineProps({
  apiUrl: {
    type: String,
    default: '/typesense'
  },
  locale: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['close']);

const isOpen = ref(false);
const searchQuery = ref('');
const searchInput = ref(null);
const loading = ref(false);
const error = ref(null);
const results = ref({
  cameras: [],
  pages: []
});

let searchTimeout = null;

// Computed properties
const hasResults = computed(() => {
  return searchQuery.value.length > 0;
});

const hasAnyResults = computed(() => {
  return results.value.pages.length > 0 || results.value.cameras.length > 0;
});

// Watch for search query changes
watch(searchQuery, (newQuery) => {
  if (newQuery.length === 0) {
    results.value = { pages: [], cameras: [] };
    error.value = null;
    return;
  }

  // Debounce search
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    performSearch(newQuery);
  }, 300);
});

// Open overlay
const open = () => {
  isOpen.value = true;
  nextTick(() => {
    searchInput.value?.focus();
  });
};

// Close overlay
const close = () => {
  isOpen.value = false;
  searchQuery.value = '';
  results.value = { pages: [], cameras: [] };
  error.value = null;
  emit('close');
};

// Perform search
const performSearch = async (query) => {
  if (!query || query.length < 2) {
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    // Product page types to prioritize
    const productPageTypes = ['App\\Pages\\ProductFamilyPage', 'App\\Pages\\ProductSeriesPage', 'App\\Pages\\SingleProductPage'];

    const localeParam = props.locale ? `&locale=${encodeURIComponent(props.locale)}` : '';

    // Search cameras
    const camerasResponse = await fetch(
      `${props.apiUrl}?q=${encodeURIComponent(query)}&collection=Cameras${localeParam}`
    );

    // Search pages (parameters are set in TypesenseController)
    const pagesResponse = await fetch(
      `${props.apiUrl}?q=${encodeURIComponent(query)}&collection=Pages${localeParam}`
    );

    if (!pagesResponse.ok || !camerasResponse.ok) {
      throw new Error('Search request failed');
    }

    const camerasData = await camerasResponse.json();
    const pagesData = await pagesResponse.json();

    // URLs to exclude from search results
    const excludedUrls = ['/lp'];

    // A record without a link cannot be opened — getRelativeLink() would turn it into "/"
    // and drop the user on the homepage, so it is left out of the results entirely.
    const hasUsableLink = hit => Boolean((hit.document.Link || '').trim());

    const filteredCameraHits = (camerasData.hits || []).filter(hasUsableLink);

    // Filter out records without a link and excluded URLs
    const filteredHits = (pagesData.hits || []).filter(hit => {
      if (!hasUsableLink(hit)) {
        return false;
      }
      return !excludedUrls.some(excluded => hit.document.Link.includes(excluded));
    });

    // Prioritize product pages by sorting
    const sortedHits = filteredHits.sort((a, b) => {
      const aIsProduct = productPageTypes.includes(a.document.ClassName);
      const bIsProduct = productPageTypes.includes(b.document.ClassName);

      // Product pages first
      if (aIsProduct && !bIsProduct) return -1;
      if (!aIsProduct && bIsProduct) return 1;

      // Keep original relevance order
      return 0;
    });

    // Store sorted hits (with highlights)
    results.value = {
      cameras: filteredCameraHits,
      pages: sortedHits
    };
  } catch (err) {
    console.error('Search error:', err);
    error.value = 'Search failed. Please try again.';
  } finally {
    loading.value = false;
  }
};

// Get highlight or fallback to original field value
const getHighlight = (hit, fieldName) => {
  // Check if highlight exists for this field
  if (hit.highlight && hit.highlight[fieldName]) {
    return hit.highlight[fieldName].snippet || hit.highlight[fieldName].value;
  }

  // Fallback to document field
  return hit.document[fieldName] || '';
};

// Extract relative path from absolute URL
const getRelativeLink = (link) => {
  if (!link) return '/';

  try {
    // If it's already a relative path, return it
    if (link.startsWith('/')) {
      return link;
    }

    // Parse URL and return pathname
    const url = new URL(link);
    return url.pathname;
  } catch (e) {
    // If URL parsing fails, return the original link
    return link;
  }
};

// Handle search input
const onSearchInput = () => {
  // Just to have a handler for template
};

// Expose methods
defineExpose({
  open,
  close
});
</script>
